import { useEffect, useState } from 'react';
import { AtSign, CheckCircle2, Mail, TriangleAlert } from 'lucide-react';
import EmailConfirmationResend from '@/components/auth/EmailConfirmationResend.jsx';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import { cn } from '@/lib/utils';
import { fetchCommunityIdentity } from '@/lib/communityIdentityClient.js';
import { isSelectiveAccountEmailVerified } from '@/lib/selectiveAccountMode.js';

export default function SelectiveAccountStatus({ user, className }) {
  const { t } = useTranslation();
  const [identityState, setIdentityState] = useState('idle');
  const [communityIdentity, setCommunityIdentity] = useState(null);

  const emailVerified = isSelectiveAccountEmailVerified(user);
  const communityHandle = typeof communityIdentity?.handle === 'string'
    ? communityIdentity.handle.trim()
    : '';

  useEffect(() => {
    let isCurrent = true;
    setCommunityIdentity(null);

    if (!user?.id) {
      setIdentityState('idle');
      return () => {
        isCurrent = false;
      };
    }

    setIdentityState('loading');
    fetchCommunityIdentity()
      .then((identity) => {
        if (!isCurrent) return;
        setCommunityIdentity(identity);
        setIdentityState('ready');
      })
      .catch(() => {
        if (!isCurrent) return;
        setIdentityState('error');
      });

    return () => {
      isCurrent = false;
    };
  }, [user?.id]);

  if (!user) {
    return null;
  }

  const handleValue = (() => {
    if (communityHandle) return `@${communityHandle}`;
    if (identityState === 'loading') return t('auth.selective_account.handle_loading');
    if (identityState === 'error') return t('auth.selective_account.handle_unavailable');
    return t('auth.selective_account.handle_pending');
  })();

  return (
    <div
      className={cn('space-y-3 rounded-lg border border-white/10 bg-black/20 p-3', className)}
      data-testid="selective-account-status"
    >
      <div className="flex items-start gap-3">
        <AtSign className="mt-0.5 h-4 w-4 shrink-0 text-white/50" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">
            {t('auth.selective_account.handle_label')}
          </p>
          <p className="mt-1 truncate text-sm text-white/85">{handleValue}</p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-white/50" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">
            {t('auth.selective_account.email_label')}
          </p>
          <p className="mt-1 truncate text-sm text-white/85">{user.email}</p>
          <div
            className={cn(
              'mt-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-medium',
              emailVerified
                ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                : 'border-amber-400/30 bg-amber-400/10 text-amber-100',
            )}
          >
            {emailVerified ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span>
              {emailVerified
                ? t('auth.selective_account.email_verified')
                : t('auth.selective_account.email_unverified')}
            </span>
          </div>
        </div>
      </div>

      {!emailVerified && user.email ? (
        <EmailConfirmationResend
          email={user.email}
          idPrefix="selective-account-email-resend"
        />
      ) : null}
    </div>
  );
}
